export type RsvpReservation = {
  eventId: number | null;
  name: string;
  email: string;
  phone: string;
  seats: number;
  reservedAt: string;
  checkedInAt?: string | null;
  source?: "online" | null;
};

type ReservationStore = Record<string, RsvpReservation>;

export const RSVP_STORE_KEY = "tih_rsvp_reservations";
export const RSVP_CHANGED_EVENT = "tih-rsvp-changed";
const LEGACY_PREFIX = "tih_rsvp_ack_";

export function reservationKey(eventId: number | null | undefined): string {
  return eventId == null ? "next" : String(eventId);
}

function canUseStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function readStore(): ReservationStore {
  if (!canUseStorage()) return {};
  try {
    const raw = window.localStorage.getItem(RSVP_STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: ReservationStore = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      const row = parseReservation(value);
      if (row) out[key] = row;
    }
    return out;
  } catch {
    return {};
  }
}

function parseReservation(raw: unknown): RsvpReservation | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const seats = typeof o.seats === "number" && Number.isFinite(o.seats) ? Math.max(1, o.seats) : 1;
  const eventId =
    typeof o.eventId === "number" && Number.isFinite(o.eventId) ? o.eventId : null;
  return {
    eventId,
    name: typeof o.name === "string" ? o.name : "",
    email: typeof o.email === "string" ? o.email : "",
    phone: typeof o.phone === "string" ? o.phone : "",
    seats,
    reservedAt: typeof o.reservedAt === "string" ? o.reservedAt : new Date().toISOString(),
    checkedInAt: typeof o.checkedInAt === "string" ? o.checkedInAt : null,
    source: o.source === "online" ? "online" : null,
  };
}

function writeStore(store: ReservationStore): void {
  if (!canUseStorage()) return;
  window.localStorage.setItem(RSVP_STORE_KEY, JSON.stringify(store));
  emitRsvpChanged();
}

function emitRsvpChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(RSVP_CHANGED_EVENT));
}

function isDetailed(row: RsvpReservation): boolean {
  return Boolean(row.name.trim() || row.email.trim() || row.phone.trim() || row.seats > 1);
}

function migrateLegacyAck(eventId: number | null | undefined): void {
  if (!canUseStorage() || eventId == null) return;
  const legacy = window.localStorage.getItem(`${LEGACY_PREFIX}${eventId}`);
  if (legacy !== "1") return;
  const store = readStore();
  const key = reservationKey(eventId);
  if (!store[key]) {
    store[key] = {
      eventId,
      name: "",
      email: "",
      phone: "",
      seats: 1,
      reservedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(RSVP_STORE_KEY, JSON.stringify(store));
  }
  window.localStorage.removeItem(`${LEGACY_PREFIX}${eventId}`);
}

export function getRsvpReservation(
  eventId: number | null | undefined,
): RsvpReservation | null {
  migrateLegacyAck(eventId);
  const store = readStore();
  const direct = store[reservationKey(eventId)];
  if (direct) return direct;
  if (eventId != null) {
    const fallback = store.next;
    if (fallback && (fallback.eventId == null || fallback.eventId === eventId)) {
      return fallback;
    }
  }
  return null;
}

export function saveRsvpReservation(input: {
  eventId: number | null;
  name: string;
  email: string;
  phone: string;
  seats: number;
}): RsvpReservation {
  const existing = getRsvpReservation(input.eventId);
  const reservation: RsvpReservation = {
    eventId: input.eventId,
    name: input.name.trim(),
    email: input.email.trim(),
    phone: input.phone.trim(),
    seats: Math.max(1, Math.min(20, Math.round(input.seats) || 1)),
    reservedAt: new Date().toISOString(),
    checkedInAt: existing?.checkedInAt ?? null,
    source: existing?.source ?? null,
  };
  const store = readStore();
  store[reservationKey(input.eventId)] = reservation;
  if (input.eventId != null) {
    store.next = reservation;
  }
  writeStore(store);
  return reservation;
}

/** True when this device has a reservation for the event (or shared "next"). */
export function getRsvpAck(eventId: number | null | undefined): boolean {
  return getRsvpReservation(eventId) != null;
}

/**
 * Intent-only ack (1 seat, empty name). Does not wipe a detailed reservation.
 */
export function setRsvpAck(eventId: number | null | undefined): void {
  const existing = getRsvpReservation(eventId);
  if (existing && isDetailed(existing)) return;
  saveRsvpReservation({
    eventId: eventId ?? null,
    name: existing?.name ?? "",
    email: existing?.email ?? "",
    phone: existing?.phone ?? "",
    seats: existing?.seats ?? 1,
  });
}

/** Mark this device as checked in online without wiping reservation details. */
export function markOnlineCheckIn(input: {
  eventId: number | null;
  name: string;
  email: string;
  phone: string;
}): RsvpReservation {
  const existing = getRsvpReservation(input.eventId);
  const reservation: RsvpReservation = {
    eventId: input.eventId,
    name: input.name.trim() || existing?.name || "",
    email: input.email.trim() || existing?.email || "",
    phone: input.phone.trim() || existing?.phone || "",
    seats: existing?.seats ?? 1,
    reservedAt: existing?.reservedAt ?? new Date().toISOString(),
    checkedInAt: new Date().toISOString(),
    source: "online",
  };
  const store = readStore();
  store[reservationKey(input.eventId)] = reservation;
  if (input.eventId != null) store.next = reservation;
  writeStore(store);
  return reservation;
}
